/**
 * Shared template IDs and options for public page template selection.
 * Used by FirstRestaurantWizard and RestaurantContentView.
 * businessType: 'restaurants' | 'services' | 'ecommerce' | 'general' (for display/filter).
 */

export const ALLOWED_TEMPLATES = ['template-1', 'template-2', 'template-3', 'template-4', 'template-5']

/** Template → business type for UI labels and filtering. */
export const TEMPLATE_BUSINESS_TYPES = {
  'template-1': 'restaurants',
  'template-2': 'services',
  'template-3': 'general',
  'template-4': 'general',
  'template-5': 'ecommerce',
}

/** Options shown in template picker. */
export const TEMPLATE_OPTIONS = [
  { id: 'template-1', name: 'Template 1', description: 'Warm, card-based layout with accent color and rounded sections.', businessType: 'restaurants' },
  { id: 'template-2', name: 'Template 2', description: 'Minimal, clean layout with simple typography and flat sections.', businessType: 'services' },
  { id: 'template-3', name: 'Template 3', description: 'Dark fine-dining style with gold accents and full-bleed hero.', businessType: 'general' },
  { id: 'template-4', name: 'Template 4', description: 'Warm cream background with bold typography and storefront-style sections.', businessType: 'general' },
  { id: 'template-5', name: 'Template 5', description: 'Clean slate layout with product-style grid, suited for ecommerce.', businessType: 'ecommerce' },
]

/** Business type -> allowed template IDs in selector UIs. */
export const BUSINESS_TYPE_TEMPLATE_IDS = {
  restaurants: ['template-1', 'template-3', 'template-4'],
  services: ['template-2', 'template-3', 'template-4'],
  ecommerce: ['template-3', 'template-4', 'template-5'],
}

/**
 * Allowed template IDs for a business type.
 * Falls back to restaurants set when unknown.
 * @param {string} businessType
 * @returns {string[]}
 */
export function getAllowedTemplateIdsForBusinessType(businessType) {
  return BUSINESS_TYPE_TEMPLATE_IDS[businessType] || BUSINESS_TYPE_TEMPLATE_IDS.restaurants
}

/**
 * Filter full template options list for a business type.
 * @param {string} businessType
 * @returns {Array<{id: string, name: string, description: string, businessType: string}>}
 */
export function getTemplateOptionsForBusinessType(businessType) {
  const allowed = new Set(getAllowedTemplateIdsForBusinessType(businessType))
  return TEMPLATE_OPTIONS.filter((opt) => allowed.has(opt.id))
}

/**
 * Build template preview image URL. Preview images live in public/template-previews/{id}.png.
 * @param {string} baseUrl - Base URL (e.g. import.meta.env.BASE_URL with trailing slash removed)
 * @param {string} templateId - One of ALLOWED_TEMPLATES
 * @returns {string|null} URL or null if templateId not allowed
 */
export function getTemplatePreviewUrl(baseUrl, templateId) {
  if (!ALLOWED_TEMPLATES.includes(templateId)) return null
  const base = typeof baseUrl === 'string' ? baseUrl.replace(/\/$/, '') : ''
  return `${base}/template-previews/${templateId}.png`
}

/**
 * Get business type for a template ID (for "Best for: X" label).
 * @param {string} templateId
 * @returns {'restaurants'|'services'|'ecommerce'|'general'}
 */
export function getTemplateBusinessType(templateId) {
  return TEMPLATE_BUSINESS_TYPES[templateId] || 'general'
}
